import { Link } from '@reach/router';
import { distanceInWordsToNow } from 'date-fns';
import React, { useContext } from 'react';
import { Query } from 'react-apollo';
import styled from 'styled-components';
import { TimeContext } from '../../../context/time';
import { Measurement } from '../../../lib/helpers/measurement';
import { MeasurementHelper } from '../../../lib/helpers/measurementHelper';
import {
  MeasurementsQuery,
  MeasurementsQueryVariables
} from '../../../queries/__generated__/MeasurementsQuery';
import { measurementsQuery } from '../../../queries/measurements';
import { variables } from '../../utils';
import ErrorScreen from '../../utils/ErrorScreen';
import BinaryLeft from './BinaryLeft';
import BinaryRight from './BinaryRight';
import FiverLeft from './FiverLeft';
import FiverRight from './FiverRight';
import IntegerLeft from './NumberLeft';
import IntegerRight from './NumberRight';
import TrafficLightLeft from './TrafficLightLeft';
import TrafficLightRight from './TrafficLightRight';

export interface IChartCardLeftProps {
  helper: MeasurementHelper;
}

export interface IChartCardRightProps {
  helper: MeasurementHelper;
  range: any;
  measurementCount: number;
}

const LeftCol = styled.div.attrs({
  className: 'flex-none flex-ns flex-column justify-between w-third-ns pt4 pb3'
})`
  @media (max-width: ${variables.medium}px) {
    border-bottom: 1px solid ${variables.lightGray};
  }
  @media (min-width: ${variables.medium}px) {
    border-right: 1px solid ${variables.lightGray};
  }
`;

const RightCol = styled.div.attrs({
  className: 'w-two-thirds-ns pa2 pa3-ns pt4 pt4-ns'
})``;

interface IChartCardProps {
  notebookId: string;
  tracker: {
    id: string;
    slug: string;
    type: TrackerType;
    name: string;
    subjects: {
      id: string;
    }[];
  };
  subject: {
    id: string;
    name: string;
    slug: string;
  };
}

const ChartRollOutCard: React.FC<IChartCardProps> = ({ notebookId, tracker, subject }) => {
  const timeContext = useContext(TimeContext);

  return (
    <Query<MeasurementsQuery, MeasurementsQueryVariables>
      query={measurementsQuery}
      variables={{
        input: {
          notebookId,
          trackerIds: [tracker.id],
          subjectIds: [subject.id]
        }
      }}
    >
      {({ data, loading, error }) => {
        if (loading) {
          return <div />;
        }

        if (error) {
          return <ErrorScreen error={error.message} />;
        }

        const measurements = data!.measurements.map(m => new Measurement(m));

        const measurementHelper = new MeasurementHelper(measurements, timeContext.range);

        return (
          <div className="bt b--navy flex-ns mh2-ns">
            <LeftCol>
              <div>
                <Link
                  to={`/notebook/subject/${encodeURIComponent(subject.slug)}`}
                  className="db f6 fw5 blue mt0 mb1"
                >
                  {subject.name}
                </Link>
                {measurementHelper.lastMeasurement && (
                  <p className="f8 serif i mt0 mb3">
                    tracked {distanceInWordsToNow(measurementHelper.lastMeasurement.notedAt)} ago
                  </p>
                )}
              </div>

              <div>
                {tracker.type === 'FIVER' && <FiverLeft helper={measurementHelper} />}
                {tracker.type === 'INTEGER' && <IntegerLeft helper={measurementHelper} />}
                {tracker.type === 'TRAFFIC_LIGHT' && (
                  <TrafficLightLeft helper={measurementHelper} />
                )}
                {tracker.type === 'BINARY' && <BinaryLeft helper={measurementHelper} />}
              </div>
            </LeftCol>

            <RightCol>
              {tracker.type === 'FIVER' && (
                <FiverRight
                  helper={measurementHelper}
                  range={timeContext.range}
                  measurementCount={measurementHelper.measurementCount}
                />
              )}
              {tracker.type === 'TRAFFIC_LIGHT' && (
                <TrafficLightRight
                  helper={measurementHelper}
                  range={timeContext.range}
                  measurementCount={measurementHelper.measurementCount}
                />
              )}
              {tracker.type === 'BINARY' && (
                <BinaryRight
                  helper={measurementHelper}
                  range={timeContext.range}
                  measurementCount={measurementHelper.measurementCount}
                />
              )}
              {tracker.type === 'INTEGER' && (
                <IntegerRight
                  helper={measurementHelper}
                  range={timeContext.range}
                  measurementCount={measurementHelper.measurementCount}
                />
              )}
            </RightCol>
          </div>
        );
      }}
    </Query>
  );
};

export default ChartRollOutCard;
